import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Building2, Cpu, Truck, Users, MapPinned, Plus, Trash2, X, Radio, ChevronDown, Gauge, Route, MapPin, UserCog, SlidersHorizontal, Activity, Zap, IdCard, KeyRound, FileCode2, FileSpreadsheet, FileText, Table as TableIcon, Copy, LogIn, Upload, Rocket, Info } from "lucide-react";
import { VEHICLE_ICON_GROUPS, getVehicleIcon, hasVehicleIcon, vehicleIconLabel } from "@/lib/fleet";
import { exportUnitRecent } from "@/lib/dataExport";
import { toast } from "sonner";
import { AdminShell, AdminCrud, ReadTable, InvoiceDashboard } from "@/components/adminkit";
import { MODULES } from "@/lib/adminConfig";
import ZoneEditor from "@/components/ZoneEditor";
import RolesMatrix from "@/components/RolesMatrix";
import PendingDevicesPanel from "@/components/PendingDevicesPanel";
import { AssistedUnitModal } from "@/components/AssistedUnitModal";
import { FleetByCompany, PoolManager } from "@/components/FleetByCompany";
import MeterCalibrationModal from "@/components/MeterCalibrationModal";
import PoiGroupManager from "@/components/PoiGroupManager";
import { useAuth } from "@/context/AuthContext";

const ROLE_RANK = { admin: 4, gestionnaire: 3, operateur: 2, technicien: 1, client: 1, user: 1 };

const ALERT_COLS = [
  { key: "vehicle", label: "Véhicule" },
  { key: "type", label: "Type" },
  { key: "message", label: "Message" },
  { key: "severity", label: "Gravité" },
  { key: "timestamp", label: "Date", render: (r) => (r.timestamp ? new Date(r.timestamp).toLocaleString("fr-FR") : "—") },
];

const ADMIN_GROUPS = [
  { group: "Flotte", items: [{ k: "by_company", label: "Par société" }, { k: "pools", label: "Groupes" }] },
  { group: "Paramètres généraux", items: [{ k: "trip_types", label: "Types de Trajet" }, { k: "trip_costs", label: "Coût de Trajet" }] },
  { group: "Paramètres utilisateur", items: [{ k: "users", label: "Gestion utilisateur" }, { k: "roles", label: "Rôles & permissions" }] },
  { group: "Listes d'actions", items: [{ k: "units", label: "Unités" }, { k: "alerts", label: "Alertes" }] },
  { group: "Paramètres conducteur", items: [{ k: "drivers", label: "Gestion Conducteur" }, { k: "driver_allocations", label: "Allocation de conducteur" }] },
  { group: "Paramètres véhicule", items: [{ k: "units", label: "Gestion Véhicule" }, { k: "billing", label: "Coûts & Factures" }, { k: "ipc", label: "Gestion IPC" }, { k: "io", label: "Entrées / Sorties" }, { k: "consumables", label: "Consommables" }, { k: "fuel_log", label: "Journal de carburant" }, { k: "assets", label: "Gestion Actif" }] },
  { group: "Paramètres emplacement", items: [{ k: "geofences", label: "Gestion Emplacement" }, { k: "routes", label: "Gestion itinéraire" }] },
  { group: "Paramètres système", items: [{ k: "companies", label: "Compagnies" }, { k: "devices", label: "Boîtiers" }, { k: "automations", label: "Automatisations" }] },
];

const EVENT_TYPES = [
  { v: "*", l: "Tous les événements" },
  { v: "exces_vitesse", l: "Excès de vitesse" },
  { v: "zone_entree", l: "Entrée de zone" },
  { v: "zone_sortie", l: "Sortie de zone" },
  { v: "arret_prolonge", l: "Arrêt prolongé" },
  { v: "ralenti", l: "Ralenti prolongé" },
  { v: "batterie", l: "Batterie faible" },
  { v: "reseau", l: "Perte réseau télécom" },
  { v: "contact", l: "Contact moteur" },
];

// Capteurs configurables (activation + libellé personnalisé) — inspiré des fiches de config Boomerang
const SENSOR_DEFS = [
  { key: "ext_voltage", label: "Alimentation", hint: "Tension externe (V)" },
  { key: "battery", label: "Batterie Interne", hint: "Niveau batterie interne (%)" },
  { key: "gsm", label: "Qualité Signal GSM", hint: "Force du signal GSM" },
  { key: "satellites", label: "Satellites", hint: "Nombre de satellites GPS" },
];
const defaultSensors = () => Object.fromEntries(SENSOR_DEFS.map((s) => [s.key, { enabled: true, label: s.label }]));

// Entrées / Sorties (I/O) — inspiré des fiches de config Boomerang
const IO_DEFS = [
  { key: "ignition", name: "Contact (Ignition)", remote: false, system: true },
  { key: "output1", name: "Sortie à distance", remote: true, system: false },
];
const defaultIo = () => IO_DEFS.map((io) => ({ ...io, enabled: io.key === "ignition" }));
const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// Permissions par fonctionnalité (RBAC)
const FEATURE_DEFS = [
  { key: "map", label: "Carte / Live Tracking" },
  { key: "history", label: "Historique & Alertes" },
  { key: "commands", label: "Commandes (position, redémarrage…)" },
  { key: "immobilizer", label: "Immobilisation (bloquer le démarrage)" },
  { key: "reports", label: "Rapports & Tableau de bord" },
  { key: "invoices", label: "Factures & Coûts" },
  { key: "tickets", label: "Tickets clients" },
  { key: "interventions", label: "Interventions technicien" },
  { key: "pois_manage", label: "Gestion des POI (créer / modifier / importer)" },
  { key: "admin", label: "Administration" },
  { key: "users", label: "Gestion des utilisateurs" },
];
const FEATURE_KEYS = FEATURE_DEFS.map((f) => f.key);
const ROLE_DEFAULTS = {
  admin: Object.fromEntries(FEATURE_KEYS.map((k) => [k, true])),
  gestionnaire: { map: true, history: true, commands: true, reports: true, invoices: true, tickets: true, interventions: true, pois_manage: true, admin: false, users: false },
  operateur: { map: true, history: true, commands: true, reports: false, invoices: false, tickets: true, interventions: false, pois_manage: true, admin: false, users: false },
  client: { map: true, history: true, commands: false, reports: true, invoices: true, tickets: true, interventions: false, pois_manage: true, admin: false, users: false },
  technicien: { map: true, history: false, commands: false, reports: false, invoices: false, tickets: false, interventions: true, pois_manage: false, admin: false, users: false },
  basic: { map: true, history: true, commands: true, reports: false, invoices: false, tickets: false, interventions: false, pois_manage: false, admin: false, users: false },
};
const ROLE_OPTIONS = [
  { v: "admin", l: "Administrateur" },
  { v: "gestionnaire", l: "Gestionnaire" },
  { v: "operateur", l: "Opérateur" },
  { v: "client", l: "Client (lecture)" },
  { v: "technicien", l: "Technicien" },
  { v: "basic", l: "Basic (Particulier)" },
];
const defaultScope = () => ({ mode: "all", vehicle_ids: [], company_ids: [] });

const Field = ({ label, children }) => (
  <label className="block mb-3">
    <span className="label-mono block mb-1.5">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";

function Modal({ title, onClose, onSubmit, children, submitLabel = "Enregistrer", wide = false }) {
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className={`glass rounded-lg w-full ${wide ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-md"} p-6 animate-slidein`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          {children}
          <button type="submit" data-testid="modal-submit" className="w-full mt-2 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold py-2.5 rounded-md text-sm transition-colors">{submitLabel}</button>
        </form>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-md mb-3 overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`section-${testid}`}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-secondary/40 hover:bg-secondary/70 transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold">{Icon && <Icon className="w-4 h-4 brand-teal" />}{title}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-3 border-t border-border">{children}</div>}
    </div>
  );
}

const CheckField = ({ label, checked, onChange, testid, disabled }) => (
  <label className={`flex items-center gap-2 py-1.5 text-sm ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => !disabled && onChange(e.target.checked)} data-testid={testid}
      className="w-4 h-4 accent-[#17A2B8]" />
    <span>{label}</span>
  </label>
);

const ReadRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-1">
    <span className="text-muted-foreground">{label}</span>
    <span className="data font-semibold text-right">{value}</span>
  </div>
);

// P0.2.5 — âge relatif pour diagnostic GPRS/GPS (aucune requête, données déjà dans /vehicles).
const relAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - Date.parse(iso)) / 1000);
  if (s < 0) return "à l'instant";
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
};
const gprsStale = (iso) => iso && (Date.now() - Date.parse(iso)) > 300000;

// Diagnostic tension d'alimentation externe — dernière valeur connue (mV -> V). Aucune requête,
// donnée déjà dans /vehicles (last_position). Affichage NEUTRE : pas de seuil couleur (12 V vs 24 V).
const fmtVolt = (u) => {
  const mv = u?.last_position?.external_voltage;
  return (mv != null && mv > 0) ? `${(mv / 1000).toFixed(1).replace(".", ",")} V` : "—";
};
const voltTs = (u) => {
  const mv = u?.last_position?.external_voltage;
  if (mv == null || mv <= 0) return "Aucune mesure de tension";
  const ts = u?.last_position?.sensor_ts?.external_voltage || u?.last_position?.timestamp;
  return ts ? `Dernière mesure : ${new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : "Dernière tension connue";
};

export default function AdminPage() {
  const { user: me, can } = useAuth();
  const isAdminUser = me?.role === "admin";
  const visibleGroups = isAdminUser ? ADMIN_GROUPS : ADMIN_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !["companies", "devices"].includes(i.k)) }));
  const [tab, setTab] = useState(isAdminUser ? "companies" : "trip_types");
  const [assistedOpen, setAssistedOpen] = useState(false);
  const [data, setData] = useState({ companies: [], devices: [], units: [], geofences: [], users: [], automations: [], drivers: [], scores: [], pois: [] });
  const [models, setModels] = useState({});
  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [resetUid, setResetUid] = useState(null);
  const [form, setForm] = useState({});
  const [poiEdit, setPoiEdit] = useState(null);
  const [poiHover, setPoiHover] = useState(null);
  const [poiImport, setPoiImport] = useState(null);  // { company_id, file, result }
  const [unitCompany, setUnitCompany] = useState("");
  const [unitSearch, setUnitSearch] = useState("");
  const [deviceCompany, setDeviceCompany] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const filteredUnits = useMemo(() => (data.units || []).filter((u) =>
    (!unitCompany || u.company_id === unitCompany) &&
    (!unitSearch || [u.name, u.plate, u.imei, u.sim_number, u.device_name, u.model, u.type].some((x) => (x || "").toString().toLowerCase().includes(unitSearch.trim().toLowerCase())))
  ), [data.units, unitCompany, unitSearch]);
  const filteredDevices = useMemo(() => (data.devices || []).filter((d) =>
    (!deviceCompany || d.company_id === deviceCompany) &&
    (!deviceSearch || [d.name, d.imei, d.sim_number, d.model, d.protocol, d.assigned_unit].some((x) => (x || "").toString().toLowerCase().includes(deviceSearch.trim().toLowerCase())))
  ), [data.devices, deviceCompany, deviceSearch]);
  const [poiImporting, setPoiImporting] = useState(false);
  const [rolesDefs, setRolesDefs] = useState({});
  const [roleList, setRoleList] = useState([]);
  const roleDef = (r) => rolesDefs[r] || ROLE_DEFAULTS[r] || ROLE_DEFAULTS.operateur;
  const roleOpts = roleList.length ? roleList : ROLE_OPTIONS;

  const loadAll = useCallback(async () => {
    // Chargement résilient : un endpoint refusé (ex. /users, /roles réservés admin
    // pour un gestionnaire) ne doit PAS vider les autres listes (POI, zones…).
    const results = await Promise.allSettled([
      api.get("/companies"), api.get("/devices"), api.get("/vehicles"),
      api.get("/geofences"), api.get("/users"), api.get("/device-models"), api.get("/automations"), api.get("/drivers"),
      api.get("/analytics/eco-scores?hours=168"), api.get("/pois"), api.get("/roles"),
    ]);
    const val = (i, fb) => (results[i].status === "fulfilled" ? results[i].value.data : fb);
    setData({
      companies: val(0, []), devices: val(1, []), units: val(2, []),
      geofences: val(3, []), users: val(4, []), automations: val(6, []),
      drivers: val(7, []), scores: val(8, {}), pois: val(9, []),
    });
    setModels(val(5, []));
    const rl = val(10, { roles: [] });
    setRolesDefs(Object.fromEntries((rl.roles || []).map((r) => [r.role, r.permissions])));
    setRoleList((rl.roles || []).map((r) => ({ v: r.role, l: r.label })));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const purgeInvalid = async () => {
    if (!window.confirm("Supprimer toutes les positions GPS aberrantes (fix insuffisant, coordonnées 0,0 ou hors limites) et recalculer les distances de toutes les unités ?")) return;
    try {
      const r = await api.post("/positions/purge-invalid");
      toast.success(`Nettoyage terminé : ${r.data.deleted_positions} position(s) aberrante(s) supprimée(s), ${r.data.vehicles_recomputed} unité(s) recalculée(s).`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors du nettoyage"); }
  };

  const openModal = (type) => {
    const defaults = {
      company: { timezone: "Pacific/Noumea" }, device: { model: "FMC130" }, unit: { type: "camion", max_speed_kmh: 90, odometer_km: 0, engine_hours: 0, usage_metric: "distance", maintenance_interval_km: 10000, maintenance_interval_hours: 500, color: "#17A2B8", icon: "truck", admin_status: "actif", unit_function: "vehicle", hidden_on_live: false, info: "", use_internal_odometer: false, service_expiry: "", trip_interval_on_min: 10, trip_interval_off_min: 10, min_distance_off_m: 250, min_speed_on_kmh: 2, min_speed_off_kmh: 20, max_speed_on_kmh: 250, max_speed_off_kmh: 250, move_min_points: 3, move_min_duration_s: 60, move_min_distance_m: 150, move_min_satellites: 5, move_notify_cooldown_min: 15, driver_continue_trip: false, driver_keep: false, driver_remove_on_off: false, driver_ignore_unknown: false, driver_id_field: "", fuel_type: "", license: "", notes: "", eco_driving_enabled: false, eco_accel_threshold: 30, eco_brake_threshold: 30, eco_corner_threshold: 30, eco_overspeed_kmh: 0, sensors: defaultSensors(), io_ports: defaultIo(), can_adapter: "none" },
      geofence: { type: "circle", kind: "autorisee", color: "#F5821F", radius: 300 },
      automation: { name: "", enabled: true, event_type: "*", min_severity: "any", vehicle_ids: [], channels: { in_app: true, email: false, sms: false } },
      driver: { name: "", phone: "", license_number: "", badge_id: "", ibutton_id: "", rfid_id: "", company_id: "", status: "actif", vehicle_id: "" },
      user: { name: "", email: "", password: "", role: "operateur", company_id: "", permissions: { ...roleDef("operateur") }, vehicle_scope: defaultScope(), twofa_enabled: true },
    };
    setEditId(null);
    setForm(defaults[type] || {});
    setModal(type);
  };

  const openEdit = (type, r) => {
    setEditId(r.id || r.user_id);
    if (type === "user") setForm({
      name: r.name,
      role: roleOpts.some((o) => o.v === r.role) ? r.role : "operateur",
      company_id: r.company_id || "",
      permissions: r.permissions && Object.keys(r.permissions).length ? { ...r.permissions } : { ...roleDef(r.role) },
      vehicle_scope: r.vehicle_scope ? { mode: r.vehicle_scope.mode || "all", vehicle_ids: r.vehicle_scope.vehicle_ids || [], company_ids: r.vehicle_scope.company_ids || (r.vehicle_scope.company_id ? [r.vehicle_scope.company_id] : []) } : defaultScope(),
      twofa_enabled: r.twofa_enabled !== false,
    });
    if (type === "company") setForm({ name: r.name, contact_name: r.contact_name || "", phone: r.phone || "", email: r.email || "", timezone: r.timezone || "Pacific/Noumea", diesel_price_xpf_l: r.diesel_price_xpf_l ?? "", essence_price_xpf_l: r.essence_price_xpf_l ?? "", electric_price_xpf_kwh: r.electric_price_xpf_kwh ?? "" });
    if (type === "device") setForm({ name: r.name, imei: r.imei, sim_number: r.sim_number || "", model: r.model, company_id: r.company_id });
    if (type === "automation") setForm({ name: r.name, enabled: r.enabled, event_type: r.event_type, min_severity: r.min_severity, vehicle_ids: r.vehicle_ids || [], channels: r.channels || { in_app: true, email: false, sms: false } });
    if (type === "driver") setForm({ name: r.name, phone: r.phone || "", license_number: r.license_number || "", badge_id: r.badge_id || "", ibutton_id: r.ibutton_id || "", rfid_id: r.rfid_id || "", company_id: r.company_id || "", status: r.status || "actif", vehicle_id: r.vehicle_id || "" });
    if (type === "unit") setForm({
      name: r.name, plate: r.plate, type: r.type, device_id: r.device_id, driver: r.driver || "",
      max_speed_kmh: r.max_speed_kmh, odometer_km: r.odometer_km, engine_hours: r.engine_hours ?? 0, usage_metric: r.usage_metric || "distance",
      maintenance_interval_km: r.maintenance_interval_km, maintenance_interval_hours: r.maintenance_interval_hours ?? 500,
      color: r.color || "#17A2B8", icon: r.icon || "truck",
      admin_status: r.admin_status || "actif", unit_function: r.unit_function || "vehicle", hidden_on_live: !!r.hidden_on_live, info: r.info || "",
      use_internal_odometer: !!r.use_internal_odometer, service_expiry: r.service_expiry || "",
      trip_interval_on_min: r.trip_interval_on_min ?? 10, trip_interval_off_min: r.trip_interval_off_min ?? 10,
      min_distance_off_m: r.min_distance_off_m ?? 250, min_speed_on_kmh: r.min_speed_on_kmh ?? 2, min_speed_off_kmh: r.min_speed_off_kmh ?? 20,
      max_speed_on_kmh: r.max_speed_on_kmh ?? 250, max_speed_off_kmh: r.max_speed_off_kmh ?? 250,
      move_min_points: r.move_min_points ?? 3, move_min_duration_s: r.move_min_duration_s ?? 60, move_min_distance_m: r.move_min_distance_m ?? 150, move_min_satellites: r.move_min_satellites ?? 5, move_notify_cooldown_min: r.move_notify_cooldown_min ?? 15,
      driver_continue_trip: !!r.driver_continue_trip, driver_keep: !!r.driver_keep, driver_remove_on_off: !!r.driver_remove_on_off,
      driver_ignore_unknown: !!r.driver_ignore_unknown, driver_id_field: r.driver_id_field || "",
      fuel_type: r.fuel_type || "", license: r.license || "", notes: r.notes || "",
      energy_type: r.energy_type || "", hybrid_fuel_type: r.hybrid_fuel_type || "",
      fuel_consumption_l100km: r.fuel_consumption_l100km ?? "", electric_consumption_kwh100km: r.electric_consumption_kwh100km ?? "",
      fuel_price_xpf_l: r.fuel_price_xpf_l ?? "", electric_price_xpf_kwh: r.electric_price_xpf_kwh ?? "",
      eco_driving_enabled: !!r.eco_driving_enabled, eco_accel_threshold: r.eco_accel_threshold ?? 30,
      eco_brake_threshold: r.eco_brake_threshold ?? 30, eco_corner_threshold: r.eco_corner_threshold ?? 30,
      eco_overspeed_kmh: r.eco_overspeed_kmh ?? 0,
      can_adapter: r.can_adapter || "none",
      sensors: r.sensors && Object.keys(r.sensors).length ? r.sensors : defaultSensors(),
      io_ports: r.io_ports && r.io_ports.length ? r.io_ports : defaultIo(),
      device_assigned_at: r.device_assigned_at || "",
    });
    setModal(type);
  };

  const openVehicleFiche = async (id) => {
    try { const r = await api.get(`/vehicles/${id}`); openEdit("unit", r.data); }
    catch { toast.error("Fiche véhicule introuvable"); }
  };

  // P0.2 — calibration explicite d'un compteur (km / heures). Récupère le véhicule à jour
  // (champs raw/ancrage/facteur) puis ouvre la modale. N'écrit jamais directement odometer_km.
  const [calib, setCalib] = useState(null);   // { veh, metric }
  const openCalib = async (metric) => {
    if (!editId) return;
    try { const r = await api.get(`/vehicles/${editId}`); setCalib({ veh: r.data, metric }); }
    catch { toast.error("Véhicule introuvable"); }
  };
  const refreshCounters = async () => {
    try {
      const r = await api.get(`/vehicles/${editId}`);
      setForm((f) => ({ ...f, odometer_km: r.data.odometer_km, engine_hours: r.data.engine_hours }));
    } catch { /* noop */ }
    loadAll();
  };

  const cloneRecord = (type, r) => {
    if (type === "company") {
      setEditId(null);
      setForm({ name: `${r.name || "Compagnie"} (copie)`, contact_name: r.contact_name || "", phone: r.phone || "", email: r.email || "", timezone: r.timezone || "Pacific/Noumea" });
      setModal("company");
      toast.info("Configuration copiée — ajustez le nom puis enregistrez.");
      return;
    }
    openEdit(type, r);
    setEditId(null);
    setForm((f) => {
      const c = { ...f };
      if (type === "unit") { c.name = `${c.name || "Unité"} (copie)`; c.plate = ""; c.device_id = ""; }
      if (type === "user") { c.name = `${(c.name || "").trim()} (copie)`.trim(); c.email = ""; c.password = ""; }
      return c;
    });
    setModal(type);
    toast.info(type === "unit" ? "Configuration copiée — choisissez un boîtier libre et l'immatriculation." : "Utilisateur copié — saisissez le nouvel email.");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // R4 : "" -> null, "0" -> 0, "123.5" -> 123.5 (ne JAMAIS détruire le zéro via Number(x)||null).
  const numOrNull = (v) => { if (v === "" || v === null || v === undefined) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
  const scoreMap = Object.fromEntries(((data.scores?.drivers) || []).filter((s) => s.driver_id).map((s) => [s.driver_id, s]));
  const openReset = (u) => { setResetUid(u.user_id); setForm({ password: "" }); setModal("resetpw"); };
  const submitReset = async () => {
    try { await api.post(`/users/${resetUid}/reset-password`, { password: form.password }); toast.success("Mot de passe réinitialisé"); setModal(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const setSensor = (key, patch) => setForm((f) => ({ ...f, sensors: { ...(f.sensors || {}), [key]: { ...(f.sensors?.[key] || {}), ...patch } } }));
  const setIo = (idx, patch) => setForm((f) => {
    const arr = Array.isArray(f.io_ports) ? [...f.io_ports] : [];
    arr[idx] = { ...(arr[idx] || {}), ...patch };
    return { ...f, io_ports: arr };
  });

  const submit = async () => {
    try {
      if (modal === "device") editId ? await api.put(`/devices/${editId}`, form) : await api.post("/devices", form);
      if (modal === "company") {
        const payload = { ...form,
          diesel_price_xpf_l: numOrNull(form.diesel_price_xpf_l),
          essence_price_xpf_l: numOrNull(form.essence_price_xpf_l),
          electric_price_xpf_kwh: numOrNull(form.electric_price_xpf_kwh) };
        editId ? await api.put(`/companies/${editId}`, payload) : await api.post("/companies", payload);
      }
      if (modal === "unit") {
        const payload = { ...form,
          hybrid_fuel_type: form.hybrid_fuel_type || null,
          fuel_consumption_l100km: numOrNull(form.fuel_consumption_l100km),
          electric_consumption_kwh100km: numOrNull(form.electric_consumption_kwh100km),
          fuel_price_xpf_l: numOrNull(form.fuel_price_xpf_l),
          electric_price_xpf_kwh: numOrNull(form.electric_price_xpf_kwh) };
        editId ? await api.put(`/vehicles/${editId}`, payload) : await api.post("/vehicles", payload);
      }
      if (modal === "automation") editId ? await api.put(`/automations/${editId}`, form) : await api.post("/automations", form);
      if (modal === "driver") editId ? await api.put(`/drivers/${editId}`, form) : await api.post("/drivers", form);
      if (modal === "user") {
        const scope = {
          mode: form.vehicle_scope?.mode || "all",
          vehicle_ids: form.vehicle_scope?.mode === "specific" ? (form.vehicle_scope?.vehicle_ids || []) : [],
          company_ids: form.vehicle_scope?.mode === "company" ? (form.vehicle_scope?.company_ids || []) : [],
          company_id: null,
        };
        const payload = { name: form.name, role: form.role, company_id: form.company_id || null, permissions: form.permissions, vehicle_scope: scope, twofa_enabled: form.twofa_enabled !== false };
        editId ? await api.put(`/users/${editId}`, payload) : await api.post("/users", { ...payload, email: form.email, password: form.password });
      }
      if (modal === "geofence") await api.post("/geofences", {
        name: form.name, type: "circle", kind: form.kind, color: form.color,
        radius: Number(form.radius), center: { lat: Number(form.lat), lon: Number(form.lon) },
        company_id: form.company_id || null,
      });
      toast.success(editId ? "Modifié avec succès" : "Créé avec succès");
      setModal(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const del = async (path, id) => {
    if (!window.confirm("Confirmer la suppression ?")) return;
    try { await api.delete(`${path}/${id}`); toast.success("Supprimé"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const dlRecent = async (u, fmt) => {
    try {
      const r = await api.get(`/vehicles/${u.id}/raw?hours=168`);
      if (!r.data.length) return toast.error("Aucune donnée récente pour ce boîtier");
      exportUnitRecent(u, r.data, fmt);
      toast.success(`Export ${fmt.toUpperCase()} — ${Math.min(r.data.length, 500)} points`);
    } catch { toast.error("Échec de l'export"); }
  };

  const canImpersonate = (u) => me && ROLE_RANK[me.role] > (ROLE_RANK[u.role] ?? 0) && u.user_id !== me.user_id && !me.impersonated_by;
  const impersonate = async (u) => {
    try {
      const res = await api.post(`/admin/impersonate/${u.user_id}`);
      localStorage.setItem("impersonator_name", res.data.impersonator_name || "administrateur");
      toast.success(`Connecté en tant que ${res.data.name || res.data.email}`);
      window.location.href = "/dashboard";
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de se connecter en tant que cet utilisateur"); }
  };


  const setRole = async (uid, role) => {
    await api.put(`/users/${uid}/role`, { role }); toast.success("Rôle mis à jour"); loadAll();
  };

  const savePoiEdit = async (e) => {
    e?.preventDefault?.();
    try { await api.put(`/pois/${poiEdit.id}`, poiEdit); toast.success("POI modifié"); setPoiEdit(null); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const isAdmin = me?.role === "admin";
  const downloadPoiTemplate = () => {
    const csv = "nom,latitude,longitude,categorie,rayon,couleur,note\n"
      + "Depot Ducos,-22.2758,166.4180,depot,150,#F5821F,Entrepot principal\n"
      + "Station Total Magenta,-22.2680,166.4600,station,,,\n"
      + "Client Carrefour,-22.2450,166.4900,client,80,#17A2B8,Livraison\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = "modele_import_poi.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const submitPoiImport = async (e) => {
    e?.preventDefault?.();
    if (!poiImport?.file) { toast.error("Choisissez un fichier CSV ou Excel"); return; }
    if (isAdmin && !poiImport.company_id) { toast.error("Sélectionnez la société cible"); return; }
    setPoiImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", poiImport.file);
      if (poiImport.company_id) fd.append("company_id", poiImport.company_id);
      const r = await api.post("/pois/import", fd);
      setPoiImport((s) => ({ ...s, result: r.data, file: null }));
      toast.success(`${r.data.created} POI importé(s)${r.data.skipped ? ` · ${r.data.skipped} ignoré(s)` : ""}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    }
    setPoiImporting(false);
  };

  const unassignedDevices = data.devices.filter((d) => !d.assigned_unit);

  return (
    <Layout>
      <AdminShell title="Administration" icon={Users} groups={visibleGroups} active={tab} setActive={setTab}>
      {MODULES[tab] && <AdminCrud {...MODULES[tab]} />}
      {tab === "by_company" && <FleetByCompany onOpenVehicle={openVehicleFiche} />}
      {tab === "pools" && <PoolManager />}
      {tab === "billing" && <InvoiceDashboard />}
      {tab === "roles" && <RolesMatrix onSaved={loadAll} />}
      {tab === "alerts" && <ReadTable endpoint="/alerts?limit=100" title="Alertes" description="Journal des alertes récentes." columns={ALERT_COLS} />}
      {tab === "io" && (
        <div className="max-w-xl">
          <h2 className="text-lg font-bold mb-2">Entrées / Sorties</h2>
          <p className="text-sm text-muted-foreground">Les entrées/sorties (I/O) se configurent par unité dans la fiche véhicule (section « Entrées / Sorties » de Gestion Véhicule).</p>
        </div>
      )}

      {/* COMPANIES */}
      {tab === "companies" && (
        <Section title="Compagnies" onAdd={() => openModal("company")} addTestid="add-company">
          <Table head={["Nom", "Contact", "Téléphone", "Email", ""]}>
            {data.companies.map((c) => (
              <tr key={c.id} onClick={() => openEdit("company", c)} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`company-${c.id}`}>
                <td className="px-4 py-3 font-semibold">{c.name}</td>
                <td className="px-4 py-3">{c.contact_name || "—"}</td>
                <td className="px-4 py-3">{c.phone || "—"}</td>
                <td className="px-4 py-3">{c.email || "—"}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}><div className="flex items-center gap-1 justify-end"><CloneBtn onClick={() => cloneRecord("company", c)} testid={`clone-company-${c.id}`} /><DelBtn onClick={() => del("/companies", c.id)} /></div></td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {/* DEVICES */}
      {tab === "devices" && isAdminUser && <PendingDevicesPanel companies={data.companies} vehicles={data.units} onChanged={loadAll} />}
      {tab === "devices" && (
        <Section title="Boîtiers (traceurs)" onAdd={() => openModal("device")} addTestid="add-device">
          <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="device-filters">
            <input value={deviceSearch} onChange={(e) => setDeviceSearch(e.target.value)} data-testid="device-search" placeholder="Rechercher (nom, IMEI, SIM, modèle…)"
              className="flex-1 bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm" />
            <select value={deviceCompany} onChange={(e) => setDeviceCompany(e.target.value)} data-testid="device-company-filter"
              className="bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm sm:w-64">
              <option value="">Toutes les sociétés</option>
              {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Table head={["Nom", "IMEI", "N° SIM", "Modèle", "Protocole", "Compagnie", "Unité", ""]}>
            {filteredDevices.map((d) => (
              <tr key={d.id} onClick={() => openEdit("device", d)} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`device-${d.id}`}>
                <td className="px-4 py-3 font-semibold">{d.name}</td>
                <td className="px-4 py-3">{d.imei}</td>
                <td className="px-4 py-3">{d.sim_number || "—"}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#17A2B8]/20 text-[#2ec5db]">{d.model}</span></td>
                <td className="px-4 py-3 text-xs flex items-center gap-1"><Radio className="w-3 h-3 brand-orange" />{d.protocol} · {d.transport}</td>
                <td className="px-4 py-3">{d.company_name || "—"}</td>
                <td className="px-4 py-3">{d.assigned_unit || <span className="text-muted-foreground">libre</span>}</td>
                <td className="px-4 py-3 text-right"><DelBtn onClick={(e) => { e.stopPropagation(); del("/devices", d.id); }} /></td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {/* UNITS */}
      {tab === "units" && (
        <Section title="Unités (véhicules)" onAdd={() => openModal("unit")} addTestid="add-unit"
          extra={<div className="flex flex-wrap gap-2">
            {can("install_tech") && <button onClick={() => setAssistedOpen(true)} data-testid="assisted-unit-btn" className="flex items-center gap-2 bg-[#17A2B8] text-white font-semibold px-3 py-2 rounded-md text-sm hover:opacity-90"><Rocket className="w-4 h-4" /><span className="hidden sm:inline">Création assistée par photos</span><span className="sm:hidden">Assistée</span></button>}
            <button onClick={purgeInvalid} data-testid="purge-invalid-btn" className="flex items-center gap-2 bg-secondary/60 hover:bg-secondary border border-border font-semibold px-3 py-2 rounded-md text-sm transition-colors"><Trash2 className="w-4 h-4 text-destructive" /><span className="hidden sm:inline">Nettoyer données aberrantes</span><span className="sm:hidden">Nettoyer</span></button>
          </div>}>
          <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="unit-filters">
            <input value={unitSearch} onChange={(e) => setUnitSearch(e.target.value)} data-testid="unit-search" placeholder="Rechercher (nom, immat., IMEI, SIM, boîtier…)"
              className="flex-1 bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm" />
            <select value={unitCompany} onChange={(e) => setUnitCompany(e.target.value)} data-testid="unit-company-filter"
              className="bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm sm:w-64">
              <option value="">Toutes les sociétés</option>
              {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Table head={["Icône", "Nom", "Immat.", "Type", "Boîtier", "IMEI", "N° SIM", "Chauffeur", "V. max", "Tension", "GPRS", "GPS", "Données Récentes", ""]}>
            {filteredUnits.map((u) => {
              const VIcon = getVehicleIcon(u.icon);
              return (
              <tr key={u.id} onClick={() => openEdit("unit", u)} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`unit-${u.id}`}>
                <td className="px-4 py-3"><VIcon className="w-5 h-5" style={{ color: u.color }} data-testid={`unit-icon-cell-${u.id}`} /></td>
                <td className="px-4 py-3 font-semibold flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: u.color }} />{u.name}</td>
                <td className="px-4 py-3">{u.plate}</td>
                <td className="px-4 py-3">{u.type}</td>
                <td className="px-4 py-3">{u.device_name || "—"} <span className="text-muted-foreground">({u.model})</span></td>
                <td className="px-4 py-3">{u.imei}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {u.sim_number
                    ? <button type="button" data-testid={`unit-sim-${u.id}`} title="Copier le numéro" onClick={() => { navigator.clipboard?.writeText(String(u.sim_number)); toast.success("N° SIM copié"); }} className="hover:text-[#17A2B8] transition-colors">{u.sim_number}</button>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3">{u.driver || "—"}</td>
                <td className="px-4 py-3">{u.max_speed_kmh} km/h</td>
                <td className="px-4 py-3 whitespace-nowrap" data-testid={`unit-voltage-${u.id}`} title={voltTs(u)}>{fmtVolt(u)}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${gprsStale(u.last_gprs_at) ? "text-[#EF4444]" : "text-[#10B981]"}`} data-testid={`unit-gprs-${u.id}`} title={u.last_gprs_at ? new Date(u.last_gprs_at).toLocaleString("fr-FR") : "Aucune communication"}>{relAgo(u.last_gprs_at)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" data-testid={`unit-gps-${u.id}`} title={(u.last_gps_at || u.last_position?.timestamp) ? new Date(u.last_gps_at || u.last_position.timestamp).toLocaleString("fr-FR") : "Aucune position GPS"}>{relAgo(u.last_gps_at || u.last_position?.timestamp)}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2.5" data-testid={`unit-recent-${u.id}`}>
                    <button title="Télécharger HTML" data-testid={`dl-html-${u.id}`} onClick={() => dlRecent(u, "html")} className="text-muted-foreground hover:text-[#17A2B8] transition-colors"><FileCode2 className="w-4 h-4" /></button>
                    <button title="Télécharger CSV" data-testid={`dl-csv-${u.id}`} onClick={() => dlRecent(u, "csv")} className="text-muted-foreground hover:text-[#F5821F] transition-colors"><TableIcon className="w-4 h-4" /></button>
                    <button title="Télécharger XLS" data-testid={`dl-xls-${u.id}`} onClick={() => dlRecent(u, "xls")} className="text-muted-foreground hover:text-[#10B981] transition-colors"><FileSpreadsheet className="w-4 h-4" /></button>
                    <button title="Télécharger PDF" data-testid={`dl-pdf-${u.id}`} onClick={() => dlRecent(u, "pdf")} className="text-muted-foreground hover:text-[#EF4444] transition-colors"><FileText className="w-4 h-4" /></button>
                  </div>
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}><div className="flex items-center gap-1 justify-end"><CloneBtn onClick={() => cloneRecord("unit", u)} testid={`clone-unit-${u.id}`} /><DelBtn onClick={() => del("/vehicles", u.id)} /></div></td>
              </tr>
            );})}
          </Table>
        </Section>
      )}

      {/* DRIVERS */}
      {tab === "drivers" && (
        <Section title="Conducteurs" onAdd={() => openModal("driver")} addTestid="add-driver">
          <p className="text-sm text-muted-foreground mb-3">Gérez les conducteurs, leur identification (badge / iButton) et leur affectation à une unité.</p>
          <Table head={["Nom", "Téléphone", "Permis", "Badge/iButton", "Compagnie", "Unité affectée", "Éco-score", "Statut", ""]}>
            {data.drivers.map((dr) => (
              <tr key={dr.id} onClick={() => openEdit("driver", dr)} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`driver-${dr.id}`}>
                <td className="px-4 py-3 font-semibold">{dr.name}</td>
                <td className="px-4 py-3">{dr.phone || "—"}</td>
                <td className="px-4 py-3">{dr.license_number || "—"}</td>
                <td className="px-4 py-3 data">{dr.badge_id || "—"}</td>
                <td className="px-4 py-3">{dr.company_name || "—"}</td>
                <td className="px-4 py-3">{dr.vehicle_name || "—"}</td>
                <td className="px-4 py-3">{(() => { const s = scoreMap[dr.id]; if (!s || s.score == null) return <span className="text-muted-foreground">—</span>; const c = s.score >= 80 ? "#10B981" : s.score >= 60 ? "#F59E0B" : "#EF4444"; return <span className="font-bold" style={{ color: c }} data-testid={`driver-score-${dr.id}`}>{s.score}/100</span>; })()}</td>
                <td className="px-4 py-3">{dr.status === "actif" ? <span className="text-[#10B981]">Actif</span> : <span className="text-muted-foreground">Inactif</span>}</td>
                <td className="px-4 py-3 text-right"><DelBtn onClick={(e) => { e.stopPropagation(); del("/drivers", dr.id); }} /></td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
      {tab === "drivers" && <UnknownBadges drivers={data.drivers} onEnrolled={loadAll} />}

      {/* AUTOMATIONS */}
      {tab === "automations" && (
        <Section title="Automatisations" onAdd={() => openModal("automation")} addTestid="add-automation">
          <p className="text-sm text-muted-foreground mb-3">Déclenchez une notification (dans l'application) quand un événement se produit. L'envoi par email et SMS arrive prochainement.</p>
          <Table head={["Nom", "Événement", "Gravité min", "Unités", "Canaux", "Actif", ""]}>
            {data.automations.map((a) => (
              <tr key={a.id} onClick={() => openEdit("automation", a)} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`automation-${a.id}`}>
                <td className="px-4 py-3 font-semibold">{a.name}</td>
                <td className="px-4 py-3">{EVENT_TYPES.find((e) => e.v === a.event_type)?.l || a.event_type}</td>
                <td className="px-4 py-3">{a.min_severity === "any" ? "Toutes" : a.min_severity}</td>
                <td className="px-4 py-3">{a.vehicle_ids?.length ? `${a.vehicle_ids.length} unité(s)` : "Toutes"}</td>
                <td className="px-4 py-3 text-xs">{["in_app", "email", "sms"].filter((c) => a.channels?.[c]).map((c) => c === "in_app" ? "App" : c === "email" ? "Email" : "SMS").join(", ") || "—"}</td>
                <td className="px-4 py-3">{a.enabled ? <span className="text-[#10B981] font-bold">●</span> : <span className="text-muted-foreground">○</span>}</td>
                <td className="px-4 py-3 text-right"><DelBtn onClick={(e) => { e.stopPropagation(); del("/automations", a.id); }} /></td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {/* GEOFENCES */}
      {tab === "geofences" && (
        <>
        <ZoneEditor geofences={data.geofences} pois={data.pois} highlightPoiId={poiHover} onSaved={loadAll} />
        <Section title="Zones (geofences)" onAdd={() => openModal("geofence")} addTestid="add-geofence">
          <Table head={["Nom", "Type", "Rayon", "Nature", ""]}>
            {data.geofences.map((g) => (
              <tr key={g.id} className="border-t border-border hover:bg-black/5" data-testid={`geofence-${g.id}`}>
                <td className="px-4 py-3 font-semibold flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} />{g.name}</td>
                <td className="px-4 py-3">{g.type}</td>
                <td className="px-4 py-3">{g.radius ? `${g.radius} m` : "—"}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-[11px] font-bold" style={{ color: g.kind === "interdite" ? "#EF4444" : "#10B981", background: g.kind === "interdite" ? "#EF444422" : "#10B98122" }}>{g.kind}</span>
                </td>
                <td className="px-4 py-3 text-right"><DelBtn onClick={() => del("/geofences", g.id)} /></td>
              </tr>
            ))}
          </Table>
        </Section>
        <div className="mt-8">
        <Section title="POI (Points d'Intérêt enregistrés)">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border">
            <p className="text-sm text-muted-foreground flex-1 min-w-[200px]">Créez des POI depuis la carte Live Tracking, ou <b>importez-en en masse</b> (CSV / Excel).</p>
            <button type="button" onClick={downloadPoiTemplate} data-testid="poi-template-btn"
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-border hover:bg-secondary/50">
              <FileSpreadsheet className="w-4 h-4" /> Modèle CSV
            </button>
            <button type="button" onClick={() => setPoiImport({ company_id: isAdmin ? "" : "", file: null, result: null })} data-testid="poi-import-btn"
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md bg-[#F5821F] text-white hover:opacity-90">
              <Upload className="w-4 h-4" /> Importer des POI
            </button>
          </div>
          <div className="p-4">
            <PoiGroupManager pois={data.pois} companies={data.companies} isAdmin={isAdmin}
              canManage={can("pois_manage")} onEditPoi={setPoiEdit} onChanged={loadAll} />
          </div>
        </Section>
        </div>
        {poiEdit && (
          <Modal title="Modifier le POI" onClose={() => setPoiEdit(null)} onSubmit={savePoiEdit}>
            <Field label="Nom"><input className={inputCls} data-testid="poi-edit-name" value={poiEdit.name || ""} onChange={(e) => setPoiEdit({ ...poiEdit, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Catégorie">
                <select className={inputCls} data-testid="poi-edit-category" value={poiEdit.category} onChange={(e) => setPoiEdit({ ...poiEdit, category: e.target.value })}>
                  <option value="client">Client</option><option value="depot">Dépôt</option><option value="station">Station</option><option value="autre">Autre</option>
                </select>
              </Field>
              <Field label="Couleur"><input type="color" className={inputCls + " h-[38px] p-1"} data-testid="poi-edit-color" value={poiEdit.color || "#17A2B8"} onChange={(e) => setPoiEdit({ ...poiEdit, color: e.target.value })} /></Field>
            </div>
            {poiEdit.type !== "polygon" && (
              <Field label="Rayon de zone (m) — 0 = aucun cercle"><input type="number" min="0" className={inputCls} data-testid="poi-edit-radius" value={poiEdit.radius ?? ""} onChange={(e) => setPoiEdit({ ...poiEdit, radius: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
            )}
          </Modal>
        )}
        {poiImport && (
          <Modal title="Importer des POI (CSV / Excel)" onClose={() => setPoiImport(null)} onSubmit={submitPoiImport}
            submitLabel={poiImporting ? "Import…" : "Importer"}>
            <p className="text-xs text-muted-foreground mb-1">
              Colonnes acceptées : <b>nom</b>, <b>latitude</b>, <b>longitude</b> (ou une colonne <i>coordonnees</i> « lat,lon »),
              <b> categorie</b> (client/depot/station/fournisseur/autre), <b>rayon</b>, <b>couleur</b>, <b>note</b>.
              <button type="button" onClick={downloadPoiTemplate} className="ml-1 text-[#17A2B8] font-semibold hover:underline">Télécharger le modèle</button>
            </p>
            {isAdmin && (
              <Field label="Société cible *">
                <select className={inputCls} data-testid="poi-import-company" value={poiImport.company_id}
                  onChange={(e) => setPoiImport((s) => ({ ...s, company_id: e.target.value }))}>
                  <option value="">— Sélectionner —</option>
                  {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Fichier (.csv ou .xlsx)">
              <input type="file" accept=".csv,.xlsx" data-testid="poi-import-file"
                onChange={(e) => setPoiImport((s) => ({ ...s, file: e.target.files?.[0] || null, result: null }))}
                className={inputCls + " py-2"} />
            </Field>
            {poiImport.result && (
              <div className="mt-2 rounded-md border border-border bg-secondary/30 p-3 text-xs space-y-1" data-testid="poi-import-result">
                <p className="font-semibold text-[#10B981]">{poiImport.result.created} POI importé(s) sur {poiImport.result.total} ligne(s).</p>
                {poiImport.result.skipped > 0 && (
                  <>
                    <p className="font-semibold text-[#F5821F]">{poiImport.result.skipped} ligne(s) ignorée(s) :</p>
                    <ul className="list-disc pl-5 max-h-40 overflow-y-auto text-muted-foreground">
                      {poiImport.result.errors.map((er, i) => <li key={i}>Ligne {er.line} : {er.reason}</li>)}
                    </ul>
                  </>
                )}
              </div>
            )}
          </Modal>
        )}
        </>
      )}

      {/* USERS */}
      {tab === "users" && (
        <Section title="Utilisateurs" onAdd={() => openModal("user")} addTestid="add-user">
          <p className="text-sm text-muted-foreground mb-3">Créez, modifiez ou supprimez des comptes et réinitialisez les mots de passe.</p>
          <Table head={["Nom", "Email", "Compagnie", "Rôle", "Actions"]}>
            {data.users.map((u) => (
              <tr key={u.user_id} onClick={() => openEdit("user", u)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEdit("user", u); } }}
                className="border-t border-border hover:bg-black/5 cursor-pointer focus:outline-none focus:bg-[#17A2B8]/10" data-testid={`user-${u.user_id}`}>
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3" data-testid={`user-company-${u.user_id}`}>{u.company_name || data.companies.find((c) => c.id === u.company_id)?.name || "—"}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <select value={roleOpts.some((o) => o.v === u.role) ? u.role : "operateur"} onChange={(e) => setRole(u.user_id, e.target.value)} data-testid={`role-select-${u.user_id}`} className={inputCls + " max-w-[160px]"}>
                    {roleOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-3 justify-end">
                    {canImpersonate(u) && (
                      <button onClick={() => impersonate(u)} data-testid={`impersonate-${u.user_id}`} className="text-xs text-[#F5821F] hover:underline flex items-center gap-1"><LogIn className="w-3.5 h-3.5" /> Voir comme</button>
                    )}
                    <button onClick={() => openReset(u)} data-testid={`reset-pw-${u.user_id}`} className="text-xs text-[#2ec5db] hover:underline flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Mot de passe</button>
                    <CloneBtn onClick={() => cloneRecord("user", u)} testid={`clone-user-${u.user_id}`} />
                    <DelBtn onClick={() => del("/users", u.user_id)} />
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      </AdminShell>

      {/* MODALS */}
      {modal === "company" && (
        <Modal title={editId ? "Modifier la compagnie" : "Nouvelle compagnie"} onClose={() => setModal(null)} onSubmit={submit}>
          <Field label="Nom *"><input required className={inputCls} data-testid="f-company-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Contact"><input className={inputCls} value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></Field>
          <Field label="Téléphone"><input className={inputCls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Email"><input className={inputCls} value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Fuseau horaire (rapports planifiés)">
            <select className={inputCls} data-testid="f-company-timezone" value={form.timezone || "Pacific/Noumea"} onChange={(e) => set("timezone", e.target.value)}>
              <option value="Pacific/Noumea">Pacific/Noumea (Nouvelle-Calédonie)</option>
              <option value="Pacific/Tahiti">Pacific/Tahiti (Polynésie française)</option>
            </select>
          </Field>
          <Field label="Prix diesel (XPF/L) — vide = inconnu, 0 = gratuit"><input type="number" min="0" step="any" className={inputCls} data-testid="f-company-diesel" value={form.diesel_price_xpf_l ?? ""} onChange={(e) => set("diesel_price_xpf_l", e.target.value)} /></Field>
          <Field label="Prix essence (XPF/L)"><input type="number" min="0" step="any" className={inputCls} data-testid="f-company-essence" value={form.essence_price_xpf_l ?? ""} onChange={(e) => set("essence_price_xpf_l", e.target.value)} /></Field>
          <Field label="Prix électricité (XPF/kWh)"><input type="number" min="0" step="any" className={inputCls} data-testid="f-company-elec" value={form.electric_price_xpf_kwh ?? ""} onChange={(e) => set("electric_price_xpf_kwh", e.target.value)} /></Field>
        </Modal>
      )}

      {modal === "device" && (
        <Modal title={editId ? "Modifier le boîtier" : "Nouveau boîtier"} onClose={() => setModal(null)} onSubmit={submit}>
          <Field label="Nom du boîtier *"><input required className={inputCls} data-testid="f-device-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="IMEI du boîtier *"><input required className={inputCls} data-testid="f-device-imei" value={form.imei || ""} onChange={(e) => set("imei", e.target.value)} /></Field>
          <Field label="Numéro de carte SIM"><input className={inputCls} data-testid="f-device-sim" value={form.sim_number || ""} onChange={(e) => set("sim_number", e.target.value)} /></Field>
          <Field label="Modèle *">
            <select className={inputCls} data-testid="f-device-model" value={form.model} onChange={(e) => set("model", e.target.value)}>
              {Object.entries(models).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.protocol}</option>)}
            </select>
          </Field>
          <Field label="Compagnie *">
            <select required className={inputCls} data-testid="f-device-company" value={form.company_id || ""} onChange={(e) => set("company_id", e.target.value)}>
              <option value="">— Sélectionner —</option>
              {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </Modal>
      )}

      {modal === "unit" && (
        <Modal title={editId ? "Modification unité" : "Nouvelle unité"} onClose={() => setModal(null)} onSubmit={submit} wide>
          {/* ---------- Général ---------- */}
          <CollapsibleSection title="Général" icon={Truck} defaultOpen testid="general">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom *"><input required className={inputCls} data-testid="f-unit-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
              <Field label="Immatriculation *"><input required className={inputCls} data-testid="f-unit-plate" value={form.plate || ""} onChange={(e) => set("plate", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select className={inputCls} value={form.type} onChange={(e) => { const t = e.target.value; set("type", t); const isM = ["engin", "machine", "materiel"].includes(t); set("usage_metric", isM ? "engine_hours" : "distance"); if (isM && (!form.maintenance_interval_hours || form.maintenance_interval_hours === 500)) set("maintenance_interval_hours", 250); }} data-testid="f-unit-type">
                  <option value="camion">Camion</option><option value="utilitaire">Utilitaire</option><option value="vul">VUL</option><option value="voiture">Voiture</option>
                  <option value="engin">Engin de chantier</option><option value="machine">Machine</option><option value="materiel">Matériel</option>
                </select>
              </Field>
              <Field label="Couleur de la ligne"><input type="color" className={inputCls + " h-[38px] p-1"} value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="f-unit-color" /></Field>
            </div>
            <Field label="Calcul d'usage (kilométrage ou temps moteur — pour engins/machines)">
              <select className={inputCls} value={form.usage_metric || "distance"} onChange={(e) => set("usage_metric", e.target.value)} data-testid="f-unit-usage-metric">
                <option value="distance">Kilométrique (km)</option>
                <option value="engine_hours">Temps moteur (heures)</option>
              </select>
            </Field>
            <Field label="Icône (logo affiché sur la carte, la liste et l'historique)">
              {(() => {
                const Sel = getVehicleIcon(form.icon); const known = hasVehicleIcon(form.icon);
                // Libellés COURTS pour la grille (on coupe au " / " ou " · "). Les deux « Chariot
                // élévateur » partagent le MÊME dessin (lucide Forklift) -> on les distingue par le libellé.
                const SHORT = { chariot_elevateur: "Chariot élévateur", forklift: "Chariot élév. (variante)" };
                const short = (key, label) => SHORT[key] || String(label).split(/[/·]/)[0].trim();
                // Type et icône INDÉPENDANTS : familles d'icônes « plausibles » par type, pour une simple
                // indication (jamais de modification auto du type ni des données).
                const TYPE_LABEL = { camion: "Camion", utilitaire: "Utilitaire", vul: "VUL", voiture: "Voiture", engin: "Engin de chantier", machine: "Machine", materiel: "Matériel" };
                const TYPE_EXPECTED = {
                  camion: ["truck", "citerne", "benne"],
                  utilitaire: ["van", "fourgon_moyen", "fourgon_grand"],
                  vul: ["van", "pickup", "fourgon_moyen", "fourgon_grand"],
                  voiture: ["car", "citadine", "suv", "taxi"],
                  engin: ["excavator", "mini_pelle", "pelle_hydraulique", "chargeuse", "engin", "compacteur", "dumper", "articule", "chantier", "tractor"],
                  machine: ["equipment", "groupe_electrogene", "poste_souder", "foreuse", "chariot_elevateur", "forklift", "industrie", "atelier"],
                  materiel: ["equipment", "groupe_electrogene", "benne", "atelier"],
                };
                const exp = TYPE_EXPECTED[form.type];
                const incoherent = known && form.icon && exp && !exp.includes(form.icon);
                return (
                <div data-testid="unit-icon-picker">
                  {/* Aperçu de l'icône sélectionnée (sans clé technique visible) */}
                  <div className="flex items-center gap-3 mb-2 rounded-md border border-border bg-secondary/40 px-3 py-2">
                    <span className="flex items-center justify-center h-12 w-12 rounded-md bg-[#17A2B8]/15 text-[#2ec5db] shrink-0" aria-hidden="true">
                      <Sel className="w-8 h-8" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Icône sélectionnée</p>
                      <p className="text-sm font-bold truncate" data-testid="unit-icon-preview-label">{vehicleIconLabel(form.icon) || "Icône par défaut (camion)"}</p>
                    </div>
                  </div>
                  {!known && (
                    <p className="text-[11px] text-amber-500 mb-2" data-testid="unit-icon-unknown-hint" role="status">
                      Icône non reconnue — un camion est affiché par défaut. Choisissez une icône ci-dessous (aucune donnée n'est modifiée automatiquement).
                    </p>
                  )}
                  {incoherent && (
                    <p className="text-[11px] text-amber-500 mb-2 flex items-start gap-1" data-testid="unit-icon-incoherent-hint" role="status">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>Le type « {TYPE_LABEL[form.type] || form.type} » et l'icône « {short(form.icon, vehicleIconLabel(form.icon))} » semblent peu cohérents. Vous pouvez conserver ce choix — le type et l'icône sont indépendants et rien n'est modifié automatiquement.</span>
                    </p>
                  )}
                  {/* Grille : pictogrammes agrandis + libellé court, responsive mobile */}
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {VEHICLE_ICON_GROUPS.map((g) => (
                      <div key={g.label}>
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">{g.label}</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                          {g.items.map(({ key, label, Comp }) => (
                            <button type="button" key={key} aria-label={label} title={label} aria-pressed={form.icon === key} data-testid={`unit-icon-${key}`}
                              onClick={() => set("icon", key)}
                              className={`flex flex-col items-center justify-start gap-1 rounded-lg border px-1.5 py-2 min-h-[72px] transition-colors ${form.icon === key ? "border-[#17A2B8] bg-[#17A2B8]/15 text-[#2ec5db]" : "border-border text-muted-foreground hover:text-foreground hover:border-[#17A2B8]/50"}`}>
                              <Comp className="w-7 h-7 shrink-0" />
                              <span className="text-[10px] leading-tight text-center line-clamp-2 w-full">{short(key, label)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ); })()}
            </Field>
            <Field label="Boîtier associé (balise) *">
              <select required className={inputCls} data-testid="f-unit-device" value={form.device_id || ""} onChange={(e) => set("device_id", e.target.value)}>
                <option value="">— Sélectionner un boîtier libre —</option>
                {data.devices.filter((d) => !d.assigned_unit || d.id === form.device_id).map((d) => <option key={d.id} value={d.id}>{d.name} · {d.model} · {d.imei}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Statut">
                <select className={inputCls} value={form.admin_status} onChange={(e) => set("admin_status", e.target.value)} data-testid="f-unit-status">
                  <option value="actif">Actif</option><option value="inactif">Inactif</option>
                </select>
              </Field>
              <Field label="Fonction de l'unité">
                <select className={inputCls} value={form.unit_function} onChange={(e) => set("unit_function", e.target.value)} data-testid="f-unit-function">
                  <option value="vehicle">Véhicule</option><option value="asset">Actif / matériel</option><option value="person">Personne</option>
                </select>
              </Field>
            </div>
            <Field label="Chauffeur"><input className={inputCls} data-testid="f-unit-driver" value={form.driver || ""} onChange={(e) => set("driver", e.target.value)} /></Field>
            <CheckField label="Cacher sur le direct" checked={form.hidden_on_live} onChange={(v) => set("hidden_on_live", v)} testid="f-unit-hidden" />
            <Field label="Information (max 1000)"><textarea maxLength={1000} rows={2} className={inputCls} data-testid="f-unit-info" value={form.info || ""} onChange={(e) => set("info", e.target.value)} /></Field>
          </CollapsibleSection>

          {/* ---------- Balise (boîtier associé, lecture seule) ---------- */}
          <CollapsibleSection title="Balise (boîtier associé)" icon={Cpu} testid="balise">
            {(() => {
              const dev = data.devices.find((d) => d.id === form.device_id);
              if (!dev) return <p className="text-sm text-muted-foreground" data-testid="unit-balise-empty">Sélectionnez un boîtier dans la section Général pour afficher ses informations.</p>;
              return (
                <div className="space-y-1 text-sm" data-testid="unit-balise-info">
                  <ReadRow label="Identifiant (IMEI)" value={dev.imei} />
                  <ReadRow label="Numéro de téléphone (SIM)" value={dev.sim_number || "—"} />
                  <ReadRow label="Modèle" value={dev.model} />
                  <ReadRow label="Protocole" value={`${dev.protocol || "—"}${dev.transport ? " · " + dev.transport : ""}`} />
                  {(dev.protocol || "").startsWith("Teltonika") && dev.model !== "FMC150" && (
                    <div className="border-t border-border pt-2 mt-2">
                      <Field label="Adaptateur CAN externe (RS232/RS485)">
                        <select className={inputCls} data-testid="f-unit-can-adapter" value={form.can_adapter || "none"} onChange={(e) => set("can_adapter", e.target.value)}>
                          <option value="none">Aucun (pas de CAN)</option>
                          <option value="lvcan200">LV-CAN200</option>
                          <option value="allcan300">ALL-CAN300</option>
                        </select>
                      </Field>
                      <p className="text-[11px] text-muted-foreground mt-1" data-testid="can-adapter-hint">Les champs CAN (régime, carburant, température…) n'apparaissent que si un adaptateur est réellement posé et transmet des trames CAN.</p>
                    </div>
                  )}
                  <div className="border-t border-border pt-2 mt-2">
                    <p className="label-mono mb-1.5">Historique d'assignation</p>
                    <ReadRow label="Assigné depuis" value={fmtDate(form.device_assigned_at)} />
                    <ReadRow label="Date de clôture" value="—" />
                  </div>
                </div>
              );
            })()}
          </CollapsibleSection>

          {/* ---------- Compteur / Moteur ---------- */}
          <CollapsibleSection title={form.usage_metric === "engine_hours" ? "Compteur horaire (heures moteur)" : "Compteur / Moteur"} icon={Gauge} testid="counter">
            {form.usage_metric === "engine_hours" ? (
              <>
                <p className="text-[11px] text-muted-foreground mb-2" data-testid="counter-mode-hours">Engin/machine : l'usage est suivi en <b>heures moteur</b> (pas en kilomètres).</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Heures moteur actuelles (h)">
                    {editId ? (
                      <div className="flex items-center gap-2">
                        <input type="number" readOnly value={form.engine_hours} className={`${inputCls} opacity-70 cursor-not-allowed`} data-testid="f-unit-engine-hours" />
                        <button type="button" onClick={() => openCalib("engine_hours")} data-testid="calib-open-hours" className="whitespace-nowrap text-xs font-bold text-[#17A2B8] border border-[#17A2B8]/40 rounded-md px-2.5 py-2 hover:bg-[#17A2B8]/10">Mettre à jour</button>
                      </div>
                    ) : (
                      <input type="number" className={inputCls} data-testid="f-unit-engine-hours" value={form.engine_hours} onChange={(e) => set("engine_hours", Number(e.target.value))} />
                    )}
                  </Field>
                  <Field label="Intervalle maintenance (heures)"><input type="number" className={inputCls} data-testid="f-unit-maint-hours" value={form.maintenance_interval_hours} onChange={(e) => set("maintenance_interval_hours", Number(e.target.value))} /></Field>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Compteur kilométrique (km)">
                    {editId ? (
                      <div className="flex items-center gap-2">
                        <input type="number" readOnly value={form.odometer_km} className={`${inputCls} opacity-70 cursor-not-allowed`} data-testid="f-unit-odometer" />
                        <button type="button" onClick={() => openCalib("odometer")} data-testid="calib-open-odometer" className="whitespace-nowrap text-xs font-bold text-[#17A2B8] border border-[#17A2B8]/40 rounded-md px-2.5 py-2 hover:bg-[#17A2B8]/10">Mettre à jour</button>
                      </div>
                    ) : (
                      <input type="number" className={inputCls} data-testid="f-unit-odometer" value={form.odometer_km} onChange={(e) => set("odometer_km", Number(e.target.value))} />
                    )}
                  </Field>
                  <Field label="Temps moteur (heures)">
                    {editId ? (
                      <div className="flex items-center gap-2">
                        <input type="number" readOnly value={form.engine_hours} className={`${inputCls} opacity-70 cursor-not-allowed`} data-testid="f-unit-engine-hours" />
                        <button type="button" onClick={() => openCalib("engine_hours")} data-testid="calib-open-hours" className="whitespace-nowrap text-xs font-bold text-[#17A2B8] border border-[#17A2B8]/40 rounded-md px-2.5 py-2 hover:bg-[#17A2B8]/10">Mettre à jour</button>
                      </div>
                    ) : (
                      <input type="number" className={inputCls} data-testid="f-unit-engine-hours" value={form.engine_hours} onChange={(e) => set("engine_hours", Number(e.target.value))} />
                    )}
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Intervalle maintenance (km)"><input type="number" className={inputCls} data-testid="f-unit-maint-km" value={form.maintenance_interval_km} onChange={(e) => set("maintenance_interval_km", Number(e.target.value))} /></Field>
                  <Field label="Maintenance par heures moteur"><input type="number" className={inputCls} data-testid="f-unit-maint-hours" value={form.maintenance_interval_hours} onChange={(e) => set("maintenance_interval_hours", Number(e.target.value))} /></Field>
                </div>
              </>
            )}
            {editId && (
              <p className="text-[11px] text-muted-foreground mt-2" data-testid="counter-readonly-note">
                Le compteur se met à jour tout seul avec la télémétrie. Pour corriger la valeur réelle, utilisez « Mettre à jour » — la correction devient une calibration persistante (elle n'est plus écrasée).
              </p>
            )}
            <div className="grid grid-cols-2 gap-3 items-end">
              <Field label="Expiration du service"><input type="date" className={inputCls} data-testid="f-unit-service-expiry" value={form.service_expiry || ""} onChange={(e) => set("service_expiry", e.target.value)} /></Field>
              <div className="mb-3"><CheckField label="Utiliser l'odomètre interne" checked={form.use_internal_odometer} onChange={(v) => set("use_internal_odometer", v)} testid="f-unit-internal-odo" /></div>
            </div>
          </CollapsibleSection>

          {/* ---------- Paramètres du trajet ---------- */}
          <CollapsibleSection title="Paramètres du trajet" icon={Route} testid="trip">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Intervalle trajet — Contact ON (min)"><input type="number" className={inputCls} data-testid="f-unit-trip-on" value={form.trip_interval_on_min} onChange={(e) => set("trip_interval_on_min", Number(e.target.value))} /></Field>
              <Field label="Intervalle trajet — Contact OFF (min)"><input type="number" className={inputCls} data-testid="f-unit-trip-off" value={form.trip_interval_off_min} onChange={(e) => set("trip_interval_off_min", Number(e.target.value))} /></Field>
            </div>
          </CollapsibleSection>

          {/* ---------- Points / enregistrements ---------- */}
          <CollapsibleSection title="Paramètres de points / enregistrements" icon={MapPin} testid="points">
            <Field label="Distance minimum — Contact OFF (mètres)"><input type="number" className={inputCls} data-testid="f-unit-min-dist" value={form.min_distance_off_m} onChange={(e) => set("min_distance_off_m", Number(e.target.value))} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vitesse min — Contact ON (km/h)"><input type="number" className={inputCls} data-testid="f-unit-minspd-on" value={form.min_speed_on_kmh} onChange={(e) => set("min_speed_on_kmh", Number(e.target.value))} /></Field>
              <Field label="Vitesse min — Contact OFF (km/h)"><input type="number" className={inputCls} data-testid="f-unit-minspd-off" value={form.min_speed_off_kmh} onChange={(e) => set("min_speed_off_kmh", Number(e.target.value))} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vitesse max — Contact ON (km/h)"><input type="number" className={inputCls} data-testid="f-unit-maxspd-on" value={form.max_speed_on_kmh} onChange={(e) => set("max_speed_on_kmh", Number(e.target.value))} /></Field>
              <Field label="Vitesse max — Contact OFF (km/h)"><input type="number" className={inputCls} data-testid="f-unit-maxspd-off" value={form.max_speed_off_kmh} onChange={(e) => set("max_speed_off_kmh", Number(e.target.value))} /></Field>
            </div>
            <Field label="Vitesse max autorisée (alerte excès, km/h)"><input type="number" className={inputCls} data-testid="f-unit-maxspeed" value={form.max_speed_kmh} onChange={(e) => set("max_speed_kmh", Number(e.target.value))} /></Field>
          </CollapsibleSection>

          {/* ---------- Item 2 : Confirmation anti-faux-mouvement (anti-saut GPS) ---------- */}
          <CollapsibleSection title="Mouvement non autorisé — confirmation (anti-saut GPS)" icon={MapPin} testid="move-confirm">
            <p className="label-mono mb-2">Un mouvement n'est confirmé (et ne déclenche une règle) que s'il est corroboré par plusieurs positions cohérentes. Les sauts GPS isolés à l'arrêt sont ignorés. Un déplacement réel contact coupé (remorquage) reste détecté.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Points cohérents min."><input type="number" className={inputCls} data-testid="f-unit-move-points" value={form.move_min_points} onChange={(e) => set("move_min_points", Number(e.target.value))} /></Field>
              <Field label="Durée min. (secondes)"><input type="number" className={inputCls} data-testid="f-unit-move-duration" value={form.move_min_duration_s} onChange={(e) => set("move_min_duration_s", Number(e.target.value))} /></Field>
              <Field label="Distance cumulée min. (mètres)"><input type="number" className={inputCls} data-testid="f-unit-move-distance" value={form.move_min_distance_m} onChange={(e) => set("move_min_distance_m", Number(e.target.value))} /></Field>
              <Field label="Satellites min. (qualité GPS)"><input type="number" className={inputCls} data-testid="f-unit-move-sats" value={form.move_min_satellites} onChange={(e) => set("move_min_satellites", Number(e.target.value))} /></Field>
              <Field label="Délai entre notifications (min)"><input type="number" className={inputCls} data-testid="f-unit-move-cooldown" value={form.move_notify_cooldown_min} onChange={(e) => set("move_notify_cooldown_min", Number(e.target.value))} /></Field>
            </div>
            <div className="mt-2 text-xs text-muted-foreground space-y-1" data-testid="move-confirm-help">
              <p><b>Comment ça marche :</b> un déplacement n'est confirmé que si, sur une fenêtre d'au moins <b>{form.move_min_duration_s} s</b> contenant au moins <b>{form.move_min_points} points</b>, le trajet cumule au moins <b>{form.move_min_distance_m} m</b> (vitesse &gt; 3 km/h partout). Le <b>délai de confirmation</b> est donc ≈ la durée min. ({form.move_min_duration_s} s) : une alerte hors horaires se déclenche au plus tôt après ce délai.</p>
              <p><b>Déplacements courts :</b> avec le défaut (150 m), des trajets de <b>20/50/100 m</b> ne sont PAS confirmés (évite les fausses alertes sur dérive/manœuvre). Pour les détecter, baisser « Distance cumulée min. » (ex. 30–50 m) et/ou « Durée min. » — au prix d'une sensibilité accrue aux sauts GPS.</p>
              <p><b>Qualité GPS / remorquage :</b> « Satellites min. » n'est exigé que si le boîtier remonte des satellites. Un remorquage contact coupé est détecté par la vitesse+distance, <b>sauf</b> si le boîtier remonte explicitement « pas de mouvement » (IO240=0) sur toute la fenêtre.</p>
            </div>
          </CollapsibleSection>

          {/* ---------- Éco-conduite (Green Driving) ---------- */}
          <CollapsibleSection title="Éco-conduite (Green Driving Teltonika)" icon={Gauge} testid="eco">
            <div className="mb-3"><CheckField label="Activer l'éco-conduite (accélérations, freinages, virages, excès)" checked={form.eco_driving_enabled} onChange={(v) => set("eco_driving_enabled", v)} testid="f-unit-eco-enabled" /></div>
            {form.eco_driving_enabled && (
              <>
                <p className="label-mono mb-2">Seuils de détection — un évènement du boîtier est retenu si sa valeur atteint le seuil (plus le seuil est bas, plus c'est sensible).</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Seuil accélération brusque"><input type="number" className={inputCls} data-testid="f-unit-eco-accel" value={form.eco_accel_threshold} onChange={(e) => set("eco_accel_threshold", Number(e.target.value))} /></Field>
                  <Field label="Seuil freinage excessif"><input type="number" className={inputCls} data-testid="f-unit-eco-brake" value={form.eco_brake_threshold} onChange={(e) => set("eco_brake_threshold", Number(e.target.value))} /></Field>
                  <Field label="Seuil virage brusque"><input type="number" className={inputCls} data-testid="f-unit-eco-corner" value={form.eco_corner_threshold} onChange={(e) => set("eco_corner_threshold", Number(e.target.value))} /></Field>
                  <Field label="Seuil excès de vitesse (km/h · 0 = vitesse max)"><input type="number" className={inputCls} data-testid="f-unit-eco-overspeed" value={form.eco_overspeed_kmh} onChange={(e) => set("eco_overspeed_kmh", Number(e.target.value))} /></Field>
                </div>
              </>
            )}
          </CollapsibleSection>

          {/* ---------- Conducteur ---------- */}
          <CollapsibleSection title="Paramètres du conducteur" icon={UserCog} testid="driver">
            <CheckField label="Continuer trajet — ajout d'un conducteur en cours de trajet" checked={form.driver_continue_trip} onChange={(v) => set("driver_continue_trip", v)} testid="f-unit-drv-continue" />
            <CheckField label="Maintenir conducteur" checked={form.driver_keep} onChange={(v) => set("driver_keep", v)} testid="f-unit-drv-keep" />
            <CheckField label="Retirer le conducteur (Contact OFF)" checked={form.driver_remove_on_off} onChange={(v) => set("driver_remove_on_off", v)} testid="f-unit-drv-remove" />
            <CheckField label="Ignorer le conducteur inconnu" checked={form.driver_ignore_unknown} onChange={(v) => set("driver_ignore_unknown", v)} testid="f-unit-drv-ignore" />
            <Field label="Champ d'identification du conducteur (Code ID)"><input className={inputCls} data-testid="f-unit-drv-id" value={form.driver_id_field || ""} onChange={(e) => set("driver_id_field", e.target.value)} /></Field>
          </CollapsibleSection>

          {/* ---------- Détails personnalisés ---------- */}
          <CollapsibleSection title="Détails personnalisés" icon={SlidersHorizontal} testid="custom">
            <Field label="Carburant">
              <select className={inputCls} value={form.fuel_type || ""} onChange={(e) => set("fuel_type", e.target.value)} data-testid="f-unit-fuel">
                <option value="">— Sélectionner —</option>
                <option value="essence">Essence</option><option value="diesel">Diesel</option><option value="gpl">GPL</option><option value="electrique">Électrique</option><option value="hybride">Hybride</option>
              </select>
            </Field>
            <Field label="Permis"><input className={inputCls} data-testid="f-unit-license" value={form.license || ""} onChange={(e) => set("license", e.target.value)} /></Field>
            <Field label="Notes"><textarea rows={2} className={inputCls} data-testid="f-unit-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
          </CollapsibleSection>

          {/* ---------- R4 : Énergie / Coûts ---------- */}
          <CollapsibleSection title="Énergie / Coûts (rapport carburant)" icon={SlidersHorizontal} testid="energy">
            <Field label="Type d'énergie">
              <select className={inputCls} value={form.energy_type || ""} onChange={(e) => set("energy_type", e.target.value)} data-testid="f-unit-energy-type">
                <option value="">— Sélectionner —</option>
                <option value="diesel">Diesel</option><option value="essence">Essence</option>
                <option value="hybride">Hybride</option><option value="electric">Électrique</option><option value="autre">Autre</option>
              </select>
            </Field>
            {(form.energy_type === "diesel" || form.energy_type === "essence") && (<>
              <Field label="Consommation estimative (L/100 km)"><input type="number" min="0" step="any" className={inputCls} data-testid="f-unit-conso-l" value={form.fuel_consumption_l100km ?? ""} onChange={(e) => set("fuel_consumption_l100km", e.target.value)} /></Field>
              <Field label="Prix spécifique (XPF/L) — vide = tarif société"><input type="number" min="0" step="any" className={inputCls} data-testid="f-unit-price-l" value={form.fuel_price_xpf_l ?? ""} onChange={(e) => set("fuel_price_xpf_l", e.target.value)} /></Field>
            </>)}
            {form.energy_type === "hybride" && (<>
              <Field label="Carburant thermique de l'hybride">
                <select className={inputCls} value={form.hybrid_fuel_type || ""} onChange={(e) => set("hybrid_fuel_type", e.target.value)} data-testid="f-unit-hybrid-fuel">
                  <option value="">Non renseigné</option><option value="essence">Essence</option><option value="diesel">Diesel</option>
                </select>
              </Field>
              <Field label="Consommation thermique estimative (L/100 km)"><input type="number" min="0" step="any" className={inputCls} data-testid="f-unit-conso-l" value={form.fuel_consumption_l100km ?? ""} onChange={(e) => set("fuel_consumption_l100km", e.target.value)} /></Field>
              <Field label="Prix spécifique (XPF/L) — vide = tarif société"><input type="number" min="0" step="any" className={inputCls} data-testid="f-unit-price-l" value={form.fuel_price_xpf_l ?? ""} onChange={(e) => set("fuel_price_xpf_l", e.target.value)} /></Field>
              <p className="text-xs text-muted-foreground px-1">La part électrique de l'hybride n'est pas mesurée actuellement (aucune énergie CAN kWh) : seul le carburant thermique est estimé.</p>
            </>)}
            {form.energy_type === "electric" && (<>
              <Field label="Consommation estimative (kWh/100 km)"><input type="number" min="0" step="any" className={inputCls} data-testid="f-unit-conso-kwh" value={form.electric_consumption_kwh100km ?? ""} onChange={(e) => set("electric_consumption_kwh100km", e.target.value)} /></Field>
              <Field label="Prix spécifique (XPF/kWh) — vide = tarif société"><input type="number" min="0" step="any" className={inputCls} data-testid="f-unit-price-kwh" value={form.electric_price_xpf_kwh ?? ""} onChange={(e) => set("electric_price_xpf_kwh", e.target.value)} /></Field>
            </>)}
            {form.energy_type === "autre" && (<p className="text-xs text-muted-foreground px-1">Type « Autre » : aucun calcul énergétique automatique.</p>)}
          </CollapsibleSection>

          {/* ---------- Capteurs ---------- */}
          <CollapsibleSection title="Capteurs" icon={Activity} testid="sensors">
            <p className="label-mono mb-2">Activez et renommez les capteurs affichés dans la liste et sur la fiche unité.</p>
            <div className="space-y-2" data-testid="unit-sensors">
              {SENSOR_DEFS.map((s) => {
                const cfg = form.sensors?.[s.key] || { enabled: true, label: s.label };
                return (
                  <div key={s.key} className="flex items-center gap-3 border border-border rounded-md px-3 py-2">
                    <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setSensor(s.key, { enabled: e.target.checked })} data-testid={`sensor-enable-${s.key}`} className="w-4 h-4 accent-[#17A2B8]" />
                    <div className="flex-1">
                      <input className={inputCls + " text-sm"} value={cfg.label ?? ""} placeholder={s.label} onChange={(e) => setSensor(s.key, { label: e.target.value })} data-testid={`sensor-label-${s.key}`} />
                      <span className="text-[11px] text-muted-foreground">{s.hint}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>

          {/* ---------- Entrées / Sorties ---------- */}
          <CollapsibleSection title="Entrées / Sorties" icon={Radio} testid="io">
            <p className="label-mono mb-2">Définissez les entrées/sorties (contact moteur, sortie à distance).</p>
            <div className="space-y-2" data-testid="unit-io">
              {(form.io_ports || []).map((io, idx) => (
                <div key={io.key} className="border border-border rounded-md px-3 py-2">
                  <input className={inputCls + " text-sm"} value={io.name} placeholder="Nom de l'E/S" disabled={io.system}
                    onChange={(e) => setIo(idx, { name: e.target.value })} data-testid={`io-name-${io.key}`} />
                  <div className="flex items-center gap-4 mt-1">
                    <CheckField label="Activée" checked={io.enabled} onChange={(v) => setIo(idx, { enabled: v })} testid={`io-enabled-${io.key}`} />
                    <CheckField label="Activation à distance" checked={io.remote} onChange={(v) => setIo(idx, { remote: v })} testid={`io-remote-${io.key}`} />
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        </Modal>
      )}

      {modal === "user" && (
        <Modal title={editId ? "Modifier l'utilisateur" : "Nouvel utilisateur"} onClose={() => setModal(null)} onSubmit={submit} wide>
          <Field label="Nom complet *"><input required className={inputCls} data-testid="f-user-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
          {!editId && (
            <>
              <Field label="Email *"><input required type="email" className={inputCls} data-testid="f-user-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Mot de passe *"><input required type="password" minLength={6} className={inputCls} data-testid="f-user-password" value={form.password || ""} onChange={(e) => set("password", e.target.value)} /></Field>
            </>
          )}
          <Field label="Rôle">
            <select className={inputCls} data-testid="f-user-role" value={form.role || "operateur"}
              onChange={(e) => { const r = e.target.value; setForm((f) => ({ ...f, role: r, permissions: { ...roleDef(r) } })); }}>
              {roleOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">Le rôle pré-remplit les autorisations ci-dessous (modifiables).</span>
          </Field>

          <Field label="Compagnie de rattachement">
            <select className={inputCls} data-testid="f-user-company" value={form.company_id || ""}
              onChange={(e) => set("company_id", e.target.value)}>
              <option value="">— Aucune —</option>
              {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">Compagnie à laquelle appartient l'utilisateur.</span>
          </Field>

          <div className="border border-border rounded-md p-3 mb-3" data-testid="user-2fa">
            <CheckField label="Authentification à deux facteurs (code par email à chaque connexion)"
              checked={["admin", "gestionnaire"].includes(form.role) ? true : form.twofa_enabled !== false}
              disabled={["admin", "gestionnaire"].includes(form.role)}
              onChange={(v) => set("twofa_enabled", v)} testid="f-user-2fa" />
            <p className="text-xs text-muted-foreground mt-1">
              {["admin", "gestionnaire"].includes(form.role)
                ? "Obligatoire pour les rôles admin et gestionnaire (non désactivable)."
                : "Activée par défaut. Vous pouvez la désactiver pour ce client."}
            </p>
          </div>

          <div className="border border-border rounded-md p-3 mb-3" data-testid="user-permissions">
            <p className="text-sm font-semibold mb-2">Autorisations par fonctionnalité</p>
            <div className="grid grid-cols-2 gap-x-4">
              {FEATURE_DEFS.map((f) => (
                <CheckField key={f.key} label={f.label} testid={`perm-${f.key}`}
                  checked={form.role === "admin" ? true : !!form.permissions?.[f.key]}
                  onChange={(v) => setForm((s) => ({ ...s, permissions: { ...(s.permissions || {}), [f.key]: v } }))} />
              ))}
            </div>
            {form.role === "admin" && <p className="text-xs text-[#F5821F] mt-1">Un administrateur a accès à tout (verrouillé).</p>}
          </div>

          <div className="border border-border rounded-md p-3 mb-3" data-testid="user-vehicle-scope">
            <p className="text-sm font-semibold mb-2">Véhicules accessibles</p>
            <select className={inputCls} data-testid="scope-mode" value={form.vehicle_scope?.mode || "all"}
              onChange={(e) => setForm((s) => ({ ...s, vehicle_scope: { ...(s.vehicle_scope || defaultScope()), mode: e.target.value } }))}>
              <option value="all">Tous les véhicules de la compagnie de rattachement</option>
              <option value="specific">Uniquement les véhicules sélectionnés</option>
              <option value="company">Uniquement les compagnies sélectionnées</option>
            </select>
            {form.vehicle_scope?.mode === "specific" && (
              <div className="mt-2 max-h-40 overflow-y-auto flex flex-wrap gap-1.5" data-testid="scope-vehicle-list">
                {data.units.map((v) => {
                  const sel = (form.vehicle_scope?.vehicle_ids || []).includes(v.id);
                  return (
                    <button type="button" key={v.id} data-testid={`scope-veh-${v.id}`}
                      onClick={() => setForm((s) => { const ids = new Set(s.vehicle_scope?.vehicle_ids || []); ids.has(v.id) ? ids.delete(v.id) : ids.add(v.id); return { ...s, vehicle_scope: { ...s.vehicle_scope, vehicle_ids: [...ids] } }; })}
                      className={`text-xs px-2 py-1 rounded-md border transition-colors ${sel ? "bg-[#17A2B8]/20 border-[#17A2B8] text-[#2ec5db]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      {v.name} · {v.plate}
                    </button>
                  );
                })}
                {!data.units.length && <span className="text-xs text-muted-foreground">Aucun véhicule</span>}
              </div>
            )}
            {form.vehicle_scope?.mode === "company" && (
              <div className="mt-2" data-testid="scope-company-list">
                <p className="text-[11px] text-muted-foreground mb-1.5">Uniquement les compagnies sélectionnées sont accessibles à cet utilisateur (la compagnie de rattachement n'est pas ajoutée automatiquement).</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.companies.map((c) => {
                    const sel = (form.vehicle_scope?.company_ids || []).includes(c.id);
                    return (
                      <button type="button" key={c.id} data-testid={`scope-company-${c.id}`}
                        onClick={() => setForm((s) => { const ids = new Set(s.vehicle_scope?.company_ids || []); ids.has(c.id) ? ids.delete(c.id) : ids.add(c.id); return { ...s, vehicle_scope: { ...s.vehicle_scope, company_ids: [...ids] } }; })}
                        className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${sel ? "bg-[#17A2B8]/20 border-[#17A2B8] text-[#2ec5db]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {c.name}
                      </button>
                    );
                  })}
                  {!data.companies.length && <span className="text-xs text-muted-foreground">Aucune compagnie</span>}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {modal === "resetpw" && (
        <Modal title="Réinitialiser le mot de passe" onClose={() => setModal(null)} onSubmit={submitReset}>
          <Field label="Nouveau mot de passe *"><input required type="password" minLength={6} className={inputCls} data-testid="f-reset-password" value={form.password || ""} onChange={(e) => set("password", e.target.value)} /></Field>
        </Modal>
      )}

      {modal === "driver" && (
        <Modal title={editId ? "Modifier le conducteur" : "Nouveau conducteur"} onClose={() => setModal(null)} onSubmit={submit}>
          <Field label="Nom complet *"><input required className={inputCls} data-testid="f-driver-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Téléphone"><input className={inputCls} data-testid="f-driver-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="N° de permis"><input className={inputCls} data-testid="f-driver-license" value={form.license_number || ""} onChange={(e) => set("license_number", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="iButton Dallas (1-Wire)"><input className={inputCls} data-testid="f-driver-ibutton" placeholder="ex: 1A2B3C4D" value={form.ibutton_id || ""} onChange={(e) => set("ibutton_id", e.target.value)} /></Field>
            <Field label="Badge RFID (RS232)"><input className={inputCls} data-testid="f-driver-rfid" placeholder="ex: 0006543210" value={form.rfid_id || ""} onChange={(e) => set("rfid_id", e.target.value)} /></Field>
          </div>
          <Field label="Badge (autre / hérité)"><input className={inputCls} data-testid="f-driver-badge" value={form.badge_id || ""} onChange={(e) => set("badge_id", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Compagnie">
              <select className={inputCls} data-testid="f-driver-company" value={form.company_id || ""} onChange={(e) => set("company_id", e.target.value)}>
                <option value="">—</option>
                {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Statut">
              <select className={inputCls} data-testid="f-driver-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="actif">Actif</option><option value="inactif">Inactif</option>
              </select>
            </Field>
          </div>
          <Field label="Unité affectée">
            <select className={inputCls} data-testid="f-driver-vehicle" value={form.vehicle_id || ""} onChange={(e) => set("vehicle_id", e.target.value)}>
              <option value="">— Aucune —</option>
              {data.units.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.plate}</option>)}
            </select>
          </Field>
        </Modal>
      )}

      {modal === "automation" && (
        <Modal title={editId ? "Modifier l'automatisation" : "Nouvelle automatisation"} onClose={() => setModal(null)} onSubmit={submit}>
          <Field label="Nom *"><input required className={inputCls} data-testid="f-auto-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Événement déclencheur">
            <select className={inputCls} data-testid="f-auto-event" value={form.event_type} onChange={(e) => set("event_type", e.target.value)}>
              {EVENT_TYPES.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
            </select>
          </Field>
          <Field label="Gravité minimum">
            <select className={inputCls} data-testid="f-auto-severity" value={form.min_severity} onChange={(e) => set("min_severity", e.target.value)}>
              <option value="any">Toutes</option><option value="low">Basse</option><option value="medium">Moyenne</option><option value="high">Haute</option>
            </select>
          </Field>
          <Field label="Unités concernées (aucune sélection = toutes)">
            <div className="max-h-32 overflow-y-auto border border-border rounded-md p-2 space-y-1" data-testid="f-auto-units">
              {data.units.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#17A2B8]" checked={(form.vehicle_ids || []).includes(u.id)}
                    onChange={(e) => { const s = new Set(form.vehicle_ids || []); e.target.checked ? s.add(u.id) : s.delete(u.id); set("vehicle_ids", [...s]); }}
                    data-testid={`f-auto-unit-${u.id}`} />
                  {u.name} · {u.plate}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Canaux de notification">
            <div className="space-y-0.5">
              <CheckField label="Dans l'application" checked={form.channels?.in_app} onChange={(v) => set("channels", { ...form.channels, in_app: v })} testid="f-auto-ch-inapp" />
              <label className="flex items-center gap-2 py-1.5 text-sm opacity-50 cursor-not-allowed">
                <input type="checkbox" disabled checked={!!form.channels?.email} readOnly className="w-4 h-4 accent-[#17A2B8]" />
                <span>Email <span className="text-[10px] text-[#F5821F]">(bientôt)</span></span>
              </label>
              <label className="flex items-center gap-2 py-1.5 text-sm opacity-50 cursor-not-allowed">
                <input type="checkbox" disabled checked={!!form.channels?.sms} readOnly className="w-4 h-4 accent-[#17A2B8]" />
                <span>SMS <span className="text-[10px] text-[#F5821F]">(bientôt)</span></span>
              </label>
            </div>
          </Field>
          <CheckField label="Automatisation active" checked={form.enabled} onChange={(v) => set("enabled", v)} testid="f-auto-enabled" />
        </Modal>
      )}

      {modal === "geofence" && (
        <Modal title="Nouvelle zone" onClose={() => setModal(null)} onSubmit={submit}>
          <Field label="Nom *"><input required className={inputCls} data-testid="f-geo-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude *"><input required type="number" step="any" className={inputCls} data-testid="f-geo-lat" value={form.lat || ""} onChange={(e) => set("lat", e.target.value)} /></Field>
            <Field label="Longitude *"><input required type="number" step="any" className={inputCls} data-testid="f-geo-lon" value={form.lon || ""} onChange={(e) => set("lon", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rayon (m)"><input type="number" className={inputCls} value={form.radius} onChange={(e) => set("radius", e.target.value)} /></Field>
            <Field label="Nature">
              <select className={inputCls} value={form.kind} onChange={(e) => set("kind", e.target.value)}>
                <option value="autorisee">Autorisée</option><option value="interdite">Interdite</option>
              </select>
            </Field>
          </div>
          <Field label="Compagnie (visibilité)">
            <select className={inputCls} data-testid="f-geo-company" value={form.company_id || ""} onChange={(e) => set("company_id", e.target.value)}>
              <option value="">— Globale (admin uniquement) —</option>
              {data.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </Modal>
      )}
      <AssistedUnitModal open={assistedOpen} onClose={() => setAssistedOpen(false)} companies={data.companies} devices={data.devices} onCreated={loadAll} />
      {calib && (
        <MeterCalibrationModal vehicle={calib.veh} metric={calib.metric} onClose={() => setCalib(null)} onSaved={refreshCounters} />
      )}
    </Layout>
  );
}

const Section = ({ title, onAdd, addTestid, extra, children }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-lg font-bold">{title}</h2>
      <div className="flex items-center gap-2">
        {extra}
        {onAdd && (
          <button onClick={onAdd} data-testid={addTestid} className="flex items-center gap-2 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-4 py-2 rounded-md text-sm transition-colors">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        )}
      </div>
    </div>
    <div className="border border-border rounded-lg overflow-x-auto">{children}</div>
  </div>
);

const Table = ({ head, children }) => (
  <table className="w-full text-sm">
    <thead className="bg-secondary/50"><tr className="label-mono text-left">{head.map((h, i) => <th key={i} className="px-4 py-3">{h}</th>)}</tr></thead>
    <tbody className="data">{children}</tbody>
  </table>
);

const DelBtn = ({ onClick }) => (
  <button onClick={onClick} className="text-muted-foreground hover:text-destructive p-1.5 rounded hover:bg-destructive/20 transition-colors"><Trash2 className="w-4 h-4" /></button>
);

// Enrôlement par scan : badges/iButton lus par un boîtier (Teltonika ou Cellocator)
// mais rattachés à aucun conducteur. L'admin associe la clé à un conducteur en un clic.
const UnknownBadges = ({ drivers, onEnrolled }) => {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(null);
  const loadBadges = useCallback(async () => {
    try { setRows((await api.get("/badges/unknown")).data || []); } catch { setRows([]); }
  }, []);
  useEffect(() => { loadBadges(); }, [loadBadges]);
  const enroll = async (tag) => {
    const did = sel[tag];
    if (!did) { toast.error("Choisissez un conducteur"); return; }
    setBusy(tag);
    try {
      await api.post(`/drivers/${did}/enroll`, { tag });
      toast.success("Badge associé au conducteur");
      await loadBadges();
      onEnrolled && onEnrolled();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'association"); }
    finally { setBusy(null); }
  };
  if (!rows.length) return null;
  return (
    <div className="mt-6" data-testid="unknown-badges-panel">
      <div className="flex items-center gap-2 mb-3">
        <IdCard className="w-5 h-5 text-[#F5821F]" />
        <h2 className="text-lg font-bold">Badges non reconnus</h2>
        <span className="text-xs bg-[#F5821F]/20 text-[#F5821F] px-2 py-0.5 rounded-full font-semibold" data-testid="unknown-badges-count">{rows.length}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-3">Clés iButton/badges lues par un boîtier mais non rattachées à un conducteur. Associez-les pour reconnaître le porteur (fonctionne pour Teltonika comme Cellocator).</p>
      <div className="border border-border rounded-lg overflow-x-auto">
        <Table head={["Empreinte", "Unité", "Vu le", "Associer à un conducteur", ""]}>
          {rows.map((b) => (
            <tr key={b.id} className="border-t border-border" data-testid={`unknown-badge-${b.tag}`}>
              <td className="px-4 py-3 data font-semibold">{b.tag}</td>
              <td className="px-4 py-3">{b.vehicle_name || "—"}</td>
              <td className="px-4 py-3">{b.last_seen ? new Date(b.last_seen).toLocaleString("fr-FR") : "—"}</td>
              <td className="px-4 py-3">
                <select className={inputCls} data-testid={`enroll-select-${b.tag}`} value={sel[b.tag] || ""} onChange={(e) => setSel((s) => ({ ...s, [b.tag]: e.target.value }))}>
                  <option value="">— Choisir —</option>
                  {(drivers || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => enroll(b.tag)} disabled={busy === b.tag} data-testid={`enroll-btn-${b.tag}`} className="bg-[#17A2B8] hover:bg-[#138496] disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded-md text-sm transition-colors">
                  {busy === b.tag ? "…" : "Associer"}
                </button>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </div>
  );
};

const CloneBtn = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} title="Cloner (dupliquer la configuration)" className="text-muted-foreground hover:text-[#17A2B8] p-1.5 rounded hover:bg-[#17A2B8]/20 transition-colors"><Copy className="w-4 h-4" /></button>
);
